import { argbFromHex, Hct, hexFromArgb, MaterialDynamicColors, SchemeTonalSpot } from '@material/material-color-utilities';

export type ThemeMode = 'system' | 'light' | 'dark';

export interface ThemePrefs {
  mode: ThemeMode;
  seed: string;
}

export const SEEDS = [
  { name: 'Violett', hex: '#6750a4' },
  { name: 'Blau', hex: '#0061a4' },
  { name: 'Grün', hex: '#386a20' },
  { name: 'Orange', hex: '#8b5000' },
  { name: 'Rosa', hex: '#984061' },
  { name: 'Türkis', hex: '#006a6a' },
] as const;

export const DEFAULT_PREFS: ThemePrefs = { mode: 'system', seed: SEEDS[0].hex };

const ROLES = [
  'primary',
  'onPrimary',
  'primaryContainer',
  'onPrimaryContainer',
  'secondary',
  'onSecondary',
  'secondaryContainer',
  'onSecondaryContainer',
  'tertiary',
  'onTertiary',
  'tertiaryContainer',
  'onTertiaryContainer',
  'error',
  'onError',
  'errorContainer',
  'onErrorContainer',
  'surface',
  'onSurface',
  'onSurfaceVariant',
  'surfaceDim',
  'surfaceBright',
  'surfaceContainerLowest',
  'surfaceContainerLow',
  'surfaceContainer',
  'surfaceContainerHigh',
  'surfaceContainerHighest',
  'inverseSurface',
  'inverseOnSurface',
  'inversePrimary',
  'outline',
  'outlineVariant',
  'scrim',
  'shadow',
] as const;

type Role = (typeof ROLES)[number];

const kebab = (s: string) => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** Generates the Material 3 (2025 / Expressive spec) color roles for a seed color. */
export function schemeTokens(seedHex: string, dark: boolean): Record<string, string> {
  const scheme = new SchemeTonalSpot(Hct.fromInt(argbFromHex(seedHex)), dark, 0, '2025');
  const tokens: Record<string, string> = {};
  for (const role of ROLES) {
    const color = (MaterialDynamicColors as unknown as Record<Role, { getArgb(s: SchemeTonalSpot): number }>)[role];
    tokens[`--md-sys-color-${kebab(role)}`] = hexFromArgb(color.getArgb(scheme));
  }
  return tokens;
}

/** Resolves "system" against the OS preference. */
export function isDark(mode: ThemeMode, prefersDark: boolean): boolean {
  return mode === 'dark' || (mode === 'system' && prefersDark);
}

/** Writes the color tokens to the document root and updates the browser UI color. */
export function applyTheme(prefs: ThemePrefs, prefersDark: boolean, root: HTMLElement = document.documentElement): void {
  const dark = isDark(prefs.mode, prefersDark);
  const tokens = schemeTokens(prefs.seed, dark);
  for (const [k, v] of Object.entries(tokens)) root.style.setProperty(k, v);
  root.dataset.theme = dark ? 'dark' : 'light';
  root.style.colorScheme = dark ? 'dark' : 'light';
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', tokens['--md-sys-color-surface-container'] ?? '');
}

const KEY = 'tagn.theme';

export function loadPrefs(storage: Storage = localStorage): ThemePrefs {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw) as Partial<ThemePrefs>;
    const mode = p.mode === 'light' || p.mode === 'dark' ? p.mode : 'system';
    const seed = typeof p.seed === 'string' && /^#[0-9a-f]{6}$/i.test(p.seed) ? p.seed : DEFAULT_PREFS.seed;
    return { mode, seed };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: ThemePrefs, storage: Storage = localStorage): void {
  try {
    storage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}
